import { useMemo } from 'react';

import { selectors, useAppSelector } from 'state/global-state';
import { SanityOffer } from 'state/loyalty/types';
import { isDiscountLoyaltyOffer } from 'state/loyalty/utils';

import { useServiceOffers } from '../state/service-offers-context';

import { useEnableLoyaltyServiceOffers } from './use-enable-loyalty-service-offers';

/**
 * Flag-aware applied-discount offer lookup.
 *
 * Mirrors `selectDiscountAppliedCmsOffer`: filter the source by the current
 * `appliedOffers` ids, then `.find(isDiscountLoyaltyOffer)`.
 * - OFF: returns the selector result verbatim.
 * - ON: filters the loyalty-service applied-offers batch metadata by the applied
 *   ids and returns the first discount match.
 *
 * Both paths return the same shape callers already rely on, so no consumer type
 * changes.
 */
export function useAppliedDiscountOffer(): SanityOffer | undefined {
  const { enabled } = useEnableLoyaltyServiceOffers();
  const cmsDiscountOffer = useAppSelector(selectors.loyalty.selectDiscountAppliedCmsOffer);
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const { appliedOffersBatch } = useServiceOffers();

  return useMemo(() => {
    if (!enabled) {
      return cmsDiscountOffer;
    }

    // Filter the last settle's batch metadata (held across the
    // null-on-refetch window, so the discount is not dropped) by the current
    // applied ids, then pick the discount. The ids filter gates out a removed
    // offer still lingering in the snapshot.
    const appliedIds = new Set(appliedOffers.map(({ id }) => id));
    const serviceDiscountOffer = Object.values(appliedOffersBatch.lastSettledMetadataById)
      .filter(offer => appliedIds.has(offer.loyaltyEngineId))
      .find(isDiscountLoyaltyOffer);

    // Cast the provider's `LoyaltyOffer` to the selector's shape
    return serviceDiscountOffer as SanityOffer | undefined;
  }, [enabled, appliedOffersBatch.lastSettledMetadataById, cmsDiscountOffer, appliedOffers]);
}
